<template>
  <div class="auth-page">
    <div class="auth-page__content">
      <img src="@/assets/images/header-logo.png" alt="Lamar Health" class="auth-page__logo" />
      <h1 class="auth-page__title">{{ passwordUpdated ? "Password Changed" : "Reset Password" }}</h1>

      <div class="auth-page__card">
        <div v-if="!passwordUpdated">
          <v-form @submit.prevent="updatePassword">
            <p class="text-center mb-4 text-primary weight-500">
              To complete the password reset process, please enter your new password below and confirm.
            </p>

            <v-text-field
              v-model="newPassword"
              label="New Password"
              :type="showPassword ? 'text' : 'password'"
              :error-messages="getPasswordErrorMessage"
              class="mb-4 lh--force-white-background"
              :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="showPassword = !showPassword" />

            <v-text-field
              v-model="passwordConfirmation"
              label="Confirm Password"
              :type="showPassword ? 'text' : 'password'"
              :error-messages="getPasswordMatchErrorMessage"
              class="mb-4 lh--force-white-background"
              :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
              @click:append-inner="showPassword = !showPassword" />

            <v-btn color="primary" block size="large" type="submit" class="mb-4 mt-8" :loading="showPreloader">
              Reset Password
            </v-btn>

            <v-alert v-if="errors.length > 0" type="error" variant="tonal" class="mb-4">
              <div v-for="error in errors" :key="error">{{ error }}</div>
            </v-alert>
          </v-form>
        </div>

        <div v-else class="text-center">
          <v-alert type="success" variant="tonal" class="mb-4"> Password changed successfully! </v-alert>
          <v-btn color="primary" block size="large" @click="redirectionToLoginPage"> Continue To Login </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import authService from "@/services/authService";
import { useRoute, useRouter } from "vue-router";
import { tryParseApiErrors } from "@/utils";
import { FORM_VALIDATION_PATTERNS } from "@/utils/constants";

const errors = ref([]);
const showPreloader = ref(false);
const showPassword = ref(false);
const route = useRoute();
const router = useRouter();

const formButtonClicked = ref(false);
const passwordUpdated = ref(false);

const newPassword = ref("");
const passwordConfirmation = ref("");

// Validators
const isPasswordValid = computed(() => {
  const password = newPassword.value;
  return FORM_VALIDATION_PATTERNS.PASSWORD_PATTERN.test(password);
});

const isPasswordMatchValid = computed(() => {
  return newPassword.value === passwordConfirmation.value;
});

const getPasswordErrorMessage = computed(() => {
  if (!formButtonClicked.value) return "";
  if (!newPassword.value) return "Please enter a password";
  if (!isPasswordValid.value) {
    return "Please enter a valid password (at least 11 characters, at least 1 uppercase and 1 lowercase letter, 1 number, and 1 symbol)";
  }
  return "";
});

const getPasswordMatchErrorMessage = computed(() => {
  if (!formButtonClicked.value) return "";
  if (!passwordConfirmation.value) return "Please confirm your password";
  if (!isPasswordMatchValid.value) return "Passwords must match";
  return "";
});

const updatePassword = async () => {
  formButtonClicked.value = true;

  if (isPasswordValid.value && isPasswordMatchValid.value) {
    showPreloader.value = true;
    try {
      await authService.passwordReset(newPassword.value, route.params.token);
      passwordUpdated.value = true;
      errors.value = [];
    } catch (error) {
      errors.value = tryParseApiErrors(error);
    } finally {
      showPreloader.value = false;
    }
  }
};

const redirectionToLoginPage = () => {
  router.push({ name: "login" });
};
</script>

<style lang="scss" scoped>
@import "../styles/main.scss";

.auth-page {
  display: flex;
  flex-direction: column;
  flex: 1;
  background: #ffffff;
}

.auth-page__logo {
  height: 36px;
  width: auto;
  margin-bottom: 32px;
}

.auth-page__content {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 16px 32px;
}

.auth-page__title {
  font-family: "DM Sans", sans-serif;
  font-size: 32px;
  font-weight: 700;
  color: #052e5e;
  margin-bottom: 32px;
}

.auth-page__card {
  width: 100%;
  max-width: 520px;
  background: #f4f6fa;
  border-radius: 16px;
  padding: 32px;
}

@media (max-width: 600px) {
  .auth-page__content {
    padding: 0 16px 24px;
  }

  .auth-page__logo {
    height: 32px;
    margin-bottom: 24px;
  }

  .auth-page__title {
    font-size: 26px;
    margin-bottom: 24px;
  }

  .auth-page__card {
    max-width: 420px;
    padding: 24px 20px;
  }
}
</style>
